import {
  ApiEsignMarkupField,
  ApiEsignMarkupRole,
  EsignConditionType,
  EsignFieldFilledBy,
  EsignFieldType,
} from "@/types/esign.types.ts";
import type { TagVariants } from "@skailer-com/uikit";
import { format } from "date-fns";
import type { CSSProperties } from "react";

export const ESIGN_PAGE_WIDTH = 816;

export const ESIGN_MARKUP_REFERRER_KEY = "esign_markup_referrer";

export interface SignerTypeConfig {
  labelKey: string;
}

export const signerTypeConfig: Partial<Record<EsignConditionType, SignerTypeConfig>> = {
  [EsignConditionType.affected_employee]: {
    labelKey: "core:esign_signer_employee",
  },
  [EsignConditionType.affected_employee_direct_manager]: {
    labelKey: "core:esign_signer_direct_manager",
  },
  [EsignConditionType.employee]: {
    labelKey: "core:esign_signer_specific_employee",
  },
  [EsignConditionType.affected_employee_custom_field_employee_reference]: {
    labelKey: "core:esign_signer_custom_field",
  },
};

export const activeSignerTypes: EsignConditionType[] = [
  EsignConditionType.affected_employee,
  EsignConditionType.affected_employee_direct_manager,
  EsignConditionType.employee,
  EsignConditionType.affected_employee_custom_field_employee_reference,
];

const signerValueTypes = new Set<EsignConditionType>([
  EsignConditionType.employee,
  EsignConditionType.affected_employee_custom_field_employee_reference,
]);

export function signerTypeNeedsValue(conditionType: EsignConditionType): boolean {
  return signerValueTypes.has(conditionType);
}

export function getSignerConfig(conditionType: EsignConditionType): SignerTypeConfig {
  return signerTypeConfig[conditionType] ?? signerTypeConfig[EsignConditionType.affected_employee]!;
}

export function getSignerTitle(
  role: ApiEsignMarkupRole,
  translate: (key: string) => string
): string {
  if (signerTypeNeedsValue(role.condition_type) && role.condition_entities?.[0]?.title) {
    return role.condition_entities[0].title;
  }
  return translate(getSignerConfig(role.condition_type).labelKey);
}

const esignAuthMethodLabelKeys: Record<string, string> = {
  email_otp: "core:esign_evidence_auth_email_otp",
};

export function getEsignAuthMethodLabel(
  method: string,
  translate: (key: string) => string
): string {
  const key = esignAuthMethodLabelKeys[method];
  return key ? translate(key) : "";
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

export function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

interface FieldRect {
  pos_x: number;
  pos_y: number;
  width: number;
  height: number;
}

export function fieldsOverlap(a: FieldRect, b: FieldRect): boolean {
  return (
    a.pos_x < b.pos_x + b.width &&
    b.pos_x < a.pos_x + a.width &&
    a.pos_y < b.pos_y + b.height &&
    b.pos_y < a.pos_y + a.height
  );
}

export function getFieldPositionStyle(
  field: FieldRect,
  baseWidth: number,
  baseHeight: number
): CSSProperties {
  return {
    left: `${(field.pos_x / baseWidth) * 100}%`,
    top: `${(field.pos_y / baseHeight) * 100}%`,
    width: `${(field.width / baseWidth) * 100}%`,
    height: `${(field.height / baseHeight) * 100}%`,
  };
}

export function getFieldFontSizeCqh(field: { font_size: number; height: number }): number {
  return round2((field.font_size / field.height) * 100);
}

export function getFieldFontSize(field: { font_size: number; height: number }): string {
  return `${getFieldFontSizeCqh(field)}cqh`;
}

export function getFontSizeForHeight(fieldType: EsignFieldType, height: number): number {
  const config = fieldTypeConfig[fieldType];
  const ratio = config.defaultFontSize / config.defaultHeight;
  return clamp(Math.round(height * ratio), 4, 96);
}

export function usesHandwritingFont(fieldType: EsignFieldType): boolean {
  return fieldType === EsignFieldType.signature || fieldType === EsignFieldType.initials;
}

export function getFieldSampleValue(
  fieldType: EsignFieldType,
  translate: (key: string) => string
): string {
  switch (fieldType) {
    case EsignFieldType.signature:
      return translate("core:esign_sample_signature");
    case EsignFieldType.full_name:
      return translate("core:esign_sample_full_name");
    case EsignFieldType.initials:
      return translate("core:esign_sample_initials");
    case EsignFieldType.job_title:
      return translate("core:esign_sample_job_title");
    case EsignFieldType.job_role:
      return translate("core:esign_sample_job_role");
    case EsignFieldType.job_level:
      return translate("core:esign_sample_job_level");
    case EsignFieldType.current_date:
      return format(new Date(), "dd.MM.yyyy");
    case EsignFieldType.text:
      return translate("core:esign_sample_text");
    case EsignFieldType.yes_no:
      return translate("core:esign_sample_yes_no");
  }
}

const fillableFieldTypes = new Set<EsignFieldType>([EsignFieldType.text, EsignFieldType.yes_no]);

export function isFillableFieldType(fieldType: EsignFieldType): boolean {
  return fillableFieldTypes.has(fieldType);
}

export function getFieldDisplayLabel(
  field: ApiEsignMarkupField,
  translate: (key: string) => string
): string {
  if (isFillableFieldType(field.field_type) && field.label) {
    return field.label;
  }
  return translate(fieldTypeConfig[field.field_type].labelKey);
}

export function isFieldRequiredMarkVisible(field: ApiEsignMarkupField): boolean {
  return isFillableFieldType(field.field_type) && field.is_required;
}

export function getDefaultFilledBy(fieldType: EsignFieldType): EsignFieldFilledBy {
  return isFillableFieldType(fieldType) ? EsignFieldFilledBy.signer : EsignFieldFilledBy.auto;
}

export const filledByLabelKeys: Record<EsignFieldFilledBy, string> = {
  [EsignFieldFilledBy.auto]: "core:esign_filled_by_auto",
  [EsignFieldFilledBy.sender]: "core:esign_filled_by_sender",
  [EsignFieldFilledBy.signer]: "core:esign_filled_by_signer",
};

export const filledByOrder: EsignFieldFilledBy[] = [
  EsignFieldFilledBy.sender,
  EsignFieldFilledBy.signer,
];

export const esignFontSizePresets = [8, 10, 12, 14, 16, 18, 20, 24, 28, 32, 40, 48];

export function findNonOverlappingPosition(
  obstacles: FieldRect[],
  desiredX: number,
  desiredY: number,
  width: number,
  height: number,
  maxWidth: number,
  maxHeight: number
): { pos_x: number; pos_y: number } {
  const clampX = (x: number) => clamp(x, 0, Math.max(0, maxWidth - width));
  const clampY = (y: number) => clamp(y, 0, Math.max(0, maxHeight - height));
  const fits = (x: number, y: number) =>
    !obstacles.some((o) => fieldsOverlap({ pos_x: x, pos_y: y, width, height }, o));

  const x0 = clampX(desiredX);
  const y0 = clampY(desiredY);
  if (fits(x0, y0)) return { pos_x: round2(x0), pos_y: round2(y0) };

  const step = 8;
  const maxRadius = Math.max(maxWidth, maxHeight);
  for (let radius = step; radius <= maxRadius; radius += step) {
    const candidates: Array<[number, number]> = [
      [x0, y0 + radius],
      [x0 + radius, y0],
      [x0 - radius, y0],
      [x0, y0 - radius],
      [x0 + radius, y0 + radius],
      [x0 - radius, y0 + radius],
      [x0 + radius, y0 - radius],
      [x0 - radius, y0 - radius],
    ];
    for (const [cx, cy] of candidates) {
      const x = clampX(cx);
      const y = clampY(cy);
      if (fits(x, y)) return { pos_x: round2(x), pos_y: round2(y) };
    }
  }

  return { pos_x: round2(x0), pos_y: round2(y0) };
}

const variantBoxClass: Partial<Record<TagVariants, string>> = {
  cyan: "border-cyan bg-cyan/10 text-cyan",
  violet: "border-violet bg-violet/10 text-violet",
  green: "border-green bg-green/10 text-green",
  blue: "border-blue bg-blue/10 text-blue",
  pink: "border-pink bg-pink/10 text-pink",
};

const variantMarkerClass: Partial<Record<TagVariants, string>> = {
  cyan: "text-cyan",
  violet: "text-violet",
  green: "text-green",
  blue: "text-blue",
  pink: "text-pink",
};

const signerColorPalette: TagVariants[] = ["cyan", "violet", "blue", "pink", "green"];

export function getRoleVariant(index: number): TagVariants {
  return signerColorPalette[index % signerColorPalette.length];
}

export function getRoleBoxClass(index: number): string {
  return variantBoxClass[getRoleVariant(index)] ?? variantBoxClass.cyan!;
}

export function getRoleMarkerClass(index: number): string {
  return variantMarkerClass[getRoleVariant(index)] ?? variantMarkerClass.cyan!;
}

export interface FieldTypeConfig {
  labelKey: string;
  icon: string;
  defaultWidth: number;
  defaultHeight: number;
  defaultFontSize: number;
  signable: boolean;
}

export const fieldTypeConfig: Record<EsignFieldType, FieldTypeConfig> = {
  [EsignFieldType.signature]: {
    labelKey: "core:esign_field_signature",
    icon: "fa-light fa-signature",
    defaultWidth: 150,
    defaultHeight: 45,
    defaultFontSize: 16,
    signable: true,
  },
  [EsignFieldType.full_name]: {
    labelKey: "core:esign_field_full_name",
    icon: "fa-light fa-font",
    defaultWidth: 150,
    defaultHeight: 45,
    defaultFontSize: 12,
    signable: false,
  },
  [EsignFieldType.initials]: {
    labelKey: "core:esign_field_initials",
    icon: "fa-light fa-heading",
    defaultWidth: 70,
    defaultHeight: 45,
    defaultFontSize: 16,
    signable: false,
  },
  [EsignFieldType.job_title]: {
    labelKey: "core:esign_field_job_title",
    icon: "fa-light fa-briefcase",
    defaultWidth: 150,
    defaultHeight: 45,
    defaultFontSize: 12,
    signable: false,
  },
  [EsignFieldType.job_role]: {
    labelKey: "core:job_role",
    icon: "fa-light fa-user-tie",
    defaultWidth: 150,
    defaultHeight: 45,
    defaultFontSize: 12,
    signable: false,
  },
  [EsignFieldType.job_level]: {
    labelKey: "core:job_level",
    icon: "fa-light fa-layer-group",
    defaultWidth: 150,
    defaultHeight: 45,
    defaultFontSize: 12,
    signable: false,
  },
  [EsignFieldType.current_date]: {
    labelKey: "core:esign_field_current_date",
    icon: "fa-light fa-calendar",
    defaultWidth: 120,
    defaultHeight: 45,
    defaultFontSize: 12,
    signable: false,
  },
  [EsignFieldType.text]: {
    labelKey: "core:esign_field_text",
    icon: "fa-light fa-input-text",
    defaultWidth: 200,
    defaultHeight: 45,
    defaultFontSize: 12,
    signable: false,
  },
  [EsignFieldType.yes_no]: {
    labelKey: "core:esign_field_yes_no",
    icon: "fa-light fa-square-check",
    defaultWidth: 120,
    defaultHeight: 32,
    defaultFontSize: 12,
    signable: false,
  },
};

export const fieldTypeOrder: EsignFieldType[] = [
  EsignFieldType.signature,
  EsignFieldType.full_name,
  EsignFieldType.initials,
  EsignFieldType.job_title,
  EsignFieldType.job_role,
  EsignFieldType.job_level,
  EsignFieldType.current_date,
  EsignFieldType.text,
  EsignFieldType.yes_no,
];

export const fieldMinWidth = 40;
export const fieldMinHeight = 16;
